import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import { motion } from 'framer-motion';
import { Search, CheckCircle, XCircle, Shield, Award, Users } from 'lucide-react';

const benefits = [
    { icon: Shield, title: 'Build Trust', desc: 'Recruitors and employers can verify your credentials instantly.' },
    { icon: Award, title: 'Prove Authenticity', desc: 'Stand out with a verified certificate from a recognized academy.' },
    { icon: Users, title: 'Anti-Fraud', desc: 'Prevents certificate forgery and fake credentials.' },
];

const CertificateVerification = () => {
    const [certId, setCertId] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleVerify = (e) => {
        e.preventDefault();
        if (!certId.trim()) return;

        setLoading(true);
        // Simulate API call
        setTimeout(() => {
            if (certId.toUpperCase().startsWith('PM')) {
                setResult({
                    valid: true,
                    name: 'Amit Sharma',
                    course: 'UI/UX Design with AI',
                    date: 'March 15, 2026',
                    id: certId.toUpperCase(),
                });
            } else {
                setResult({ valid: false });
            }
            setLoading(false);
        }, 1500);
    };

    return (
        <>
            <Helmet>
                <title>Certificate Verification | PyrusMedia Academy</title>
                <meta name="description" content="Verify the authenticity of PyrusMedia Academy certificates. Trusted by recruiters and employers." />
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-20 left-1/3 w-[400px] h-[400px] bg-[#bebd19]/5 rounded-full blur-[120px]" />
                <div className="container mx-auto px-4 relative z-10">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
                    >
                        Verify Your <span className="text-[#bebd19]">Certificate</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-gray-500 max-w-2xl mx-auto"
                    >
                        Enter your certificate ID to verify its authenticity. Trusted by recruiters and employers.
                    </motion.p>
                </div>
            </section>

            {/* Verification Form */}
            <Section className="bg-gray-50">
                <div className="max-w-xl mx-auto">
                    <form onSubmit={handleVerify} className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
                        <label className="block text-gray-900 font-semibold mb-3">Certificate ID</label>
                        <div className="flex gap-3">
                            <input
                                type="text"
                                value={certId}
                                onChange={(e) => { setCertId(e.target.value); setResult(null); }}
                                placeholder="e.g., PM-UX-2026-00123"
                                className="flex-1 border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none font-mono"
                            />
                            <button
                                type="submit"
                                disabled={loading || !certId.trim()}
                                className="bg-[#bebd19] text-black font-bold px-6 py-3 rounded-lg hover:bg-[#d4d325] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <Search size={18} />
                                {loading ? 'Verifying...' : 'Verify'}
                            </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">Format: PM-XX-YYYY-XXXXX (e.g., PM-UX-2026-00123)</p>
                    </form>

                    {/* Result */}
                    {result && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`mt-6 p-8 rounded-2xl border-2 ${
                                result.valid
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-red-50 border-red-200'
                            }`}
                        >
                            {result.valid ? (
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle size={32} className="text-green-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-green-800 mb-2">Certificate Verified</h3>
                                    <p className="text-green-600 mb-6">This certificate is authentic and issued by PyrusMedia Academy.</p>

                                    <div className="bg-white p-6 rounded-xl border border-green-200 text-left max-w-sm mx-auto">
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 text-sm">Student Name</span>
                                                <span className="font-semibold text-gray-900">{result.name}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 text-sm">Course</span>
                                                <span className="font-semibold text-gray-900">{result.course}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 text-sm">Completion Date</span>
                                                <span className="font-semibold text-gray-900">{result.date}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 text-sm">Certificate ID</span>
                                                <span className="font-mono font-semibold text-gray-900">{result.id}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                                        <XCircle size={32} className="text-red-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-red-800 mb-2">Certificate Not Found</h3>
                                    <p className="text-red-600">
                                        No certificate matches this ID. Please check the ID and try again, or contact support.
                                    </p>
                                </div>
                            )}
                        </motion.div>
                    )}
                </div>
            </Section>

            {/* Why Verify */}
            <Section className="bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Verify?</h2>
                <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                    {benefits.map((b, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="text-center p-6"
                        >
                            <div className="w-14 h-14 rounded-full bg-[#bebd19]/10 flex items-center justify-center mx-auto mb-4">
                                <b.icon size={24} className="text-[#bebd19]" />
                            </div>
                            <h3 className="font-bold text-gray-900 mb-2">{b.title}</h3>
                            <p className="text-sm text-gray-500">{b.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* For Employers */}
            <Section className="bg-gray-50">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">For Recruiters & Employers</h2>
                    <p className="text-gray-500 mb-8 max-w-xl mx-auto">
                        Hiring a PyrusMedia Academy graduate? Use this tool to verify their certificate.
                        Our certificates include a unique ID that can be verified in real-time.
                    </p>
                    <div className="bg-white p-8 rounded-2xl border border-gray-200">
                        <div className="grid md:grid-cols-3 gap-6 text-center">
                            <div>
                                <div className="text-2xl font-bold text-[#bebd19] mb-1">Real-time</div>
                                <div className="text-sm text-gray-500">Instant verification</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-[#bebd19] mb-1">100%</div>
                                <div className="text-sm text-gray-500">Accurate results</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-[#bebd19] mb-1">Free</div>
                                <div className="text-sm text-gray-500">No charges</div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>
        </>
    );
};

export default CertificateVerification;
